import { useEffect, useState } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useParams, useSearchParams } from "react-router-dom";
import { config } from "./Components/Config";

const QuotationApprovalResponse = () => {
  const { id_quotation } = useParams();
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action");
  const token = searchParams.get("token");

  const [quotation, setQuotation] = useState(null);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [autoProcessed, setAutoProcessed] = useState(false);

  const getQuotation = async () => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + "public/quotation-approval-status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id_quotation, token }),
      });
      const responseQuotation = await fetchQuotation.json();
      if (responseQuotation.status === 200) {
        setQuotation(responseQuotation.data);
      } else {
        setError(responseQuotation.message || "Unable to load quotation.");
      }
    } catch (err) {
      setError("Unable to load quotation.");
    }
  };

  const isFinalStatus = (currentQuotation) => {
    if (!currentQuotation) return false;
    const finalApprovals = ["Approved", "Rejected"];
    return (
      finalApprovals.includes(currentQuotation.quotation_approval) ||
      currentQuotation.quotation_status !== "Open"
    );
  };

  const submitApproval = async (status) => {
    if (!quotation) return;
    if (isFinalStatus(quotation)) {
      setMessage(`Quotation already ${quotation.quotation_approval || quotation.quotation_status}.`);
      setSubmitted(true);
      return;
    }

    if (status === "Rejected" && approvalRemarks.trim() === "") {
      setError("Remarks are required to reject the quotation.");
      return;
    }

    setLoading(true);
    setError("");

    const crmUser = localStorage.getItem("crmUser");
    let updatedBy = 0;
    try {
      updatedBy = crmUser ? JSON.parse(crmUser).user_keyid : 0;
    } catch (err) {
      updatedBy = 0;
    }

    const requestEditQuotation = {
      id_quotation: id_quotation,
      action: status,
      remarks: approvalRemarks,
      updated_by: updatedBy,
      token: token,
    };

    try {
      const fetchEditQuotation = await jwtFetch(config.ENDPOINT + "public/quotation-approval-response", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestEditQuotation),
      });
      const responseEditQuotation = await fetchEditQuotation.json();
      if (responseEditQuotation.status === 200) {
        setMessage(
          status === "Approved"
            ? "Approval has been recorded successfully."
            : "Rejection has been recorded successfully."
        );
        setSubmitted(true);
        getQuotation();
      } else {
        setError(responseEditQuotation.message || "Unable to update quotation.");
      }
    } catch (err) {
      setError("Unable to update quotation.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id_quotation) {
      getQuotation();
    }
  }, [id_quotation]);

  useEffect(() => {
    if (!quotation || !action || autoProcessed) return;

    if (action === "approve") {
      setAutoProcessed(true);
      if (isFinalStatus(quotation)) {
        setMessage(`Quotation already ${quotation.quotation_approval || quotation.quotation_status}.`);
        setSubmitted(true);
      } else {
        submitApproval("Approved");
      }
    } else if (action === "reject") {
      if (isFinalStatus(quotation)) {
        setMessage(`Quotation already ${quotation.quotation_approval || quotation.quotation_status}.`);
        setSubmitted(true);
      }
    } else {
      setError("Invalid response action. Use the approve or reject link from email.");
    }
  }, [quotation, action, autoProcessed]);

  return (
    <div id="quotation-approval-response" className="container py-5">
      <div className="card mx-auto" style={{ maxWidth: "720px" }}>
        <div className="card-body">
          <h2 className="mb-4">Quotation Approval Response</h2>

          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}

          {!quotation && !error && <div>Loading quotation details...</div>}

          {quotation && (
            <div className="mb-4">
              <div className="row mb-2">
                <div className="col-sm-4 text-secondary">Quotation</div>
                <div className="col-sm-8">{quotation.quotation_code}</div>
              </div>
              <div className="row mb-2">
                <div className="col-sm-4 text-secondary">Current Approval</div>
                <div className="col-sm-8">{quotation.quotation_approval || "N/A"}</div>
              </div>
              <div className="row mb-2">
                <div className="col-sm-4 text-secondary">Status</div>
                <div className="col-sm-8">{quotation.quotation_status}</div>
              </div>
            </div>
          )}

          {action === "reject" && quotation && !submitted && !isFinalStatus(quotation) && (
            <div>
              <div className="mb-3">
                <label className="form-label">Remarks <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={approvalRemarks}
                  onChange={(e) => setApprovalRemarks(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="d-flex justify-content-end gap-2">
                <a
                  className="btn btn-secondary"
                  href={window.location.pathname.replace("/approval-response/", "/quotation/approval/")}
                >
                  Review Quotation
                </a>
                <button
                  className="btn btn-danger"
                  type="button"
                  onClick={() => submitApproval("Rejected")}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit Rejection"}
                </button>
              </div>
            </div>
          )}

          {action === "approve" && !submitted && quotation && !isFinalStatus(quotation) && (
            <div className="alert alert-info">Processing approval. Please wait...</div>
          )}

          {submitted && quotation && (
            <div className="mt-3">
              <a
                className="btn btn-primary"
                href={`/quotation/approval/${id_quotation}`}
              >
                Open Quotation Approval Page
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotationApprovalResponse;


